import {
  FluidColor,
  FluidLanguage,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import { Component, Element, h, Prop, State } from '@stencil/core';

import { TranslatableSlot } from '../../common/fluid-component-rendering.components';
import {
  setI18nState,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';

/**
 * @displayName Title
 *
 * @slot Add title display text as the <fluid-title> content.
 *
 * @builder true
 */
@Component({
  tag: 'fluid-title',
  styleUrl: 'fluid-title.less',
  shadow: true,
})
export class FluidTitle implements FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Reference to host element - we need listen to listen to hover
   * events, even on disabled buttons
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: { content?: string };
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
  @State() _lang: FluidLanguage;
  @State() _i18nBound: boolean;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   * @type {string}
   * @api-docs: Theming
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   * @api-docs: Theming
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * What level of header should we render (i.e 1 is the equivalent of h1, and so on)
   */
  @Prop({ mutable: true }) level: string;

  /**
   * If true, the headers margins will be removed
   */
  @Prop({ mutable: true }) disableGutters: boolean;

  /**
   * Will set the color of the Title text. Based on pre-defined colors set by Liberty
   * color scheme available in FluidColor enum .
   */
  @Prop({ mutable: true }) color: FluidColor;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  render() {
    const Tag = `h${this.level ? this.level : 1}`;
    const style = {
      [Tag]: true,
      [this.theme]: true,
      'fluid-title': true,
      [this.color]: !!this.color,
      'disable-gutters': this.disableGutters,
    };
    return (
      <Tag class={style}>
        <TranslatableSlot
          store={this._i18nStore}
          translationKey={this.fluidTranslate?.content}
        />
      </Tag>
    );
  }
}
